/**
 * Vue du responsable qui intervient aussi (propriétaire, 24/10/2026) :
 * « Entreprise » pilote toute l'équipe, « Ma journée » ne montre que ses
 * propres interventions, comme à un technicien. Cachée dans le menu du compte,
 * la bascule passait inaperçue : elle est désormais en haut de la colonne sur
 * ordinateur et en tête du Menu au téléphone. Changer de vue ramène à
 * l'accueil, qui change avec elle. Choix de présentation, jamais de droits.
 */
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Truck, type LucideIcon } from 'lucide-react';
import { isFieldWorker, useAuth, type FieldMode } from '../../core/auth.js';
import { t } from '../../core/i18n.js';
import { ChoiceGroup } from '../ui/ChoiceGroup.js';
import { Section } from '../ui/Section.js';

const MODES: Record<FieldMode, { label: string; hint: string; icon: LucideIcon }> = {
  pilotage: { label: t('nav.pilotage'), hint: t('nav.pilotageDetail'), icon: Building2 },
  terrain: { label: t('nav.modeTerrain'), hint: t('nav.modeTerrainDetail'), icon: Truck },
};

const ORDER: readonly FieldMode[] = ['pilotage', 'terrain'];

/** La bascule ne concerne qu'un responsable qui va aussi sur le terrain. */
export function useWorkModeSwitchable(): boolean {
  const { me } = useAuth();
  return Boolean(me?.field_operator && !isFieldWorker(me));
}

export function WorkModeSwitch(props: {
  /** Colonne réduite : un seul bouton, l'icône de la vue actuelle. */
  collapsed?: boolean;
  /** Menu du téléphone : un bloc titré, avec une ligne sur la vue choisie. */
  explained?: boolean;
}): ReactNode {
  const { fieldMode, setFieldMode } = useAuth();
  const navigate = useNavigate();
  const switchable = useWorkModeSwitchable();
  if (!switchable) return null;
  const choose = (mode: FieldMode): void => {
    if (mode === fieldMode) return;
    setFieldMode(mode);
    navigate('/');
  };
  const current = MODES[fieldMode];

  if (props.collapsed) {
    const other: FieldMode = fieldMode === 'pilotage' ? 'terrain' : 'pilotage';
    const Icon = current.icon;
    const label = `${t('nav.mode')} : ${current.label}. ${t('nav.modeBascule', { vue: MODES[other].label })}`;
    return (
      <button type="button" className="work-mode-toggle" aria-label={label} title={label} onClick={() => choose(other)}>
        <Icon size={20} aria-hidden="true" />
      </button>
    );
  }

  // Menu du téléphone : un bloc comme les autres (titre, une ligne sur la vue
  // choisie), et les icônes des deux vues ; la colonne, plus étroite, s'en passe.
  if (props.explained) {
    return (
      <Section title={t('nav.modeTitre')} hint={current.hint} flush>
        <ChoiceGroup<FieldMode>
          label={t('nav.mode')}
          hideLabel
          value={fieldMode}
          variant="segmented"
          options={ORDER.map((mode) => {
            const Icon = MODES[mode].icon;
            return { value: mode, label: MODES[mode].label, icon: <Icon size={18} /> };
          })}
          onChange={choose}
        />
      </Section>
    );
  }

  return (
    <div className="work-mode" title={current.hint}>
      <ChoiceGroup<FieldMode>
        label={t('nav.mode')}
        hideLabel
        value={fieldMode}
        variant="segmented"
        options={ORDER.map((mode) => ({ value: mode, label: MODES[mode].label }))}
        onChange={choose}
      />
    </div>
  );
}
